import { DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime, Observable, Subject, switchMap, tap } from 'rxjs';
import { HttpServiceService } from '../services/http-service.service';

export interface PickerItem {
  value: string;
  label: string;
  data?: any;
}

/**
 * Small helper that drives an ng-select with server-side search + scroll paging.
 * Bind `[items]="picker.items"`, `[typeahead]="picker.search$"` and
 * `(scrollToEnd)="picker.scrollEnd()"`; call `picker.init()` once to load the first page.
 */
export class LazyPicker {
  items: PickerItem[] = [];
  search$ = new Subject<string>();
  private page = 1;
  private hasMore = true;
  private term = '';
  private loading = false;
  /** Preselected values that must stay in the list whatever page is loaded. */
  private pinned: PickerItem[] = [];

  constructor(
    private fetch: (page: number, limit: number, term: string) => Observable<any>,
    private map: (resp: any) => { rows: PickerItem[]; total: number },
    destroyRef: DestroyRef,
    private limit: number = 50,
  ) {
    this.search$
      .pipe(
        debounceTime(300),
        tap((term) => {
          this.page = 1;
          this.hasMore = true;
          this.term = term ?? '';
        }),
        switchMap(() => this.fetch(1, this.limit, this.term)),
        takeUntilDestroyed(destroyRef),
      )
      .subscribe({
        next: (resp) => {
          const { rows, total } = this.map(resp);
          this.items = this.withPinned(rows);
          this.hasMore = rows.length < total;
        },
      });
  }

  private withPinned(rows: PickerItem[]): PickerItem[] {
    const missing = this.pinned.filter((p) => !rows.some((r) => r.value === p.value));
    return [...missing, ...rows];
  }

  init(): void {
    this.search$.next('');
  }

  scrollEnd(): void {
    if (!this.hasMore || this.loading) return;
    this.loading = true;
    this.page++;
    this.fetch(this.page, this.limit, this.term).subscribe({
      next: (resp) => {
        const { rows, total } = this.map(resp);
        this.items = [...this.items, ...rows];
        this.hasMore = this.items.length < total;
        this.loading = false;
      },
      error: () => (this.loading = false),
    });
  }

  /** Make sure a preselected value is present in the option list. */
  ensure(item: PickerItem | null | undefined): void {
    if (!item || !item.value) return;
    if (!this.pinned.some((p) => p.value === item.value)) this.pinned.push(item);
    if (!this.items.some((i) => i.value === item.value)) {
      this.items = [item, ...this.items];
    }
  }
}

// ── Ready-made pickers for the task module ────────────────────────────────

export function staffPicker(http: HttpServiceService, destroyRef: DestroyRef, extra: PickerItem[] = []): LazyPicker {
  return new LazyPicker(
    (page, limit, term) => http.getUsersByPropertyRole(page, limit, term),
    (resp) => ({
      rows: [
        ...extra,
        ...((resp?.data?.users ?? []) as any[]).map((u) => ({
          value: u.uid,
          label: `${u.first_name ?? ''} ${u.last_name ?? ''}`.trim(),
          data: u,
        })),
      ],
      total: (resp?.data?.total ?? 0) + extra.length,
    }),
    destroyRef,
  );
}

export function propertyPicker(http: HttpServiceService, destroyRef: DestroyRef): LazyPicker {
  return new LazyPicker(
    (page, limit, term) => http.getAllProperties(page, limit, term ? `&name=${encodeURIComponent(term)}` : ''),
    (resp) => ({
      rows: ((resp?.data?.properties ?? []) as any[]).map((p) => ({
        value: p.uid,
        label: p.name,
        data: p,
      })),
      total: resp?.data?.total ?? 0,
    }),
    destroyRef,
  );
}

export function leadPicker(http: HttpServiceService, destroyRef: DestroyRef): LazyPicker {
  return new LazyPicker(
    (page, limit, term) =>
      http.getAllLeads(page, limit, 'created_at', 'DESC', term ? `&search=${encodeURIComponent(term)}` : ''),
    (resp) => ({
      rows: ((resp?.data?.leads ?? []) as any[]).map((l) => ({
        value: l.id,
        label: l.name + (l.phone ? ` · ${l.phone}` : ''),
        data: l,
      })),
      total: resp?.data?.total ?? 0,
    }),
    destroyRef,
  );
}
